import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@shared/ui/card";
import { useLocation, useParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/hooks/use-toast";
import {
  useUploadDocumentsForConsumer,
  useConsumerDetails,
} from "@features/cx/consumerManagement/hooks";
import { StepHelpers } from "@shared/components/Stepper";
import { DocumentUploadForm } from "@features/cx/shared/components/DocumentUploadForm";

// Simplified DocumentUploadForm component below

interface Document {
  id: string;
  name: string;
  type: string;
  category: string;
  subCategory: string;
  categoryCode: string;
  subCategoryCode: string;
  size: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  url?: string;
  file?: File;
  needsReupload?: boolean;
  consumerRole?: string;
  isActive?: boolean;
  documentStatus?: string;
}

interface DocumentUploadStepProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  onDataChange?: (data: any) => void;
  consumerId?: string | number | null;
  isEditMode?: boolean;
}

export function DocumentUploadStep({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating = false,
  onDataChange,
  isEditMode = false,
}: DocumentUploadStepProps) {
  const location = useLocation();
  const { consumerId } = useParams<{ consumerId: string }>();
  const [shouldFetchDocuments, setShouldFetchDocuments] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editConsumerMutation = useUploadDocumentsForConsumer();

  // Fetch consumer details with documents when hash changes
  const { data: consumerDetailsResponse, isLoading: isLoadingConsumerDetails } =
    useConsumerDetails(
      {
        consumer_id: consumerId?.toString() || "",
        remote_utility_id: String(remoteUtilityId),
        is_detail_info: true,
        is_document: true,
      },
      {
        enabled: !!consumerId && !!remoteUtilityId && isEditMode && shouldFetchDocuments,
      }
    );

  const consumerDetails = consumerDetailsResponse?.result;

  // Trigger refetch when hash changes
  useEffect(() => {
    if (location.hash.includes("document-upload")) {
      console.log("🔁 Detected hash change, triggering document fetch");
      setShouldFetchDocuments((prev) => !prev);
    }
  }, [location.hash]);

  // Initial fetch on mount for edit mode
  useEffect(() => {
    if (isEditMode) {
      setShouldFetchDocuments(true);
    }
  }, [isEditMode]);

  // Save step data helper
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("✅ Step data saved:", stepData);
      } catch (error) {
        console.error("❌ Failed to save step data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Handle document save from form
  const handleDocumentSave = useCallback(
    (uploadedDocuments: Document[]) => {
      console.log("📄 Documents saved:", uploadedDocuments);
      setDocuments(uploadedDocuments);

      const fileObjects = uploadedDocuments
        .filter((doc) => doc.file)
        .map((doc) => doc.file!)
        .filter(Boolean);

      const fileMetadata = uploadedDocuments.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
        category: doc.category,
        subCategory: doc.subCategory,
        categoryCode: doc.categoryCode,
        subCategoryCode: doc.subCategoryCode,
        size: doc.size,
        needsReupload: doc.needsReupload || false,
        url: doc.url,
      }));

      const documentData = {
        documents: uploadedDocuments.map((doc) => ({
          ...doc,
          file: doc.file ? true : undefined,
        })),
        validFileCount: uploadedDocuments.filter(
          (doc) => (doc.url || doc.file) && !doc.needsReupload
        ).length,
        totalFileCount: uploadedDocuments.length,
        updatedAt: new Date().toISOString(),
      };

      saveStepData(documentData);

      if (onDataChange) {
        onDataChange({
          documents: uploadedDocuments,
          file: fileObjects,
          fileMetadata: fileMetadata,
          notes: "",
          incidentDate: "",
          expectedResolution: "",
        });
      }
    },
    [saveStepData, onDataChange]
  );

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if (clearValidationError) {
      clearValidationError();
    }

    const validDocuments = documents.filter(
      (doc) => (doc.file || doc.url) && !doc.needsReupload
    );

    // if (validDocuments.length === 0) {
    //   const errorMessage = "Please upload at least one document to continue.";
    //   toast({
    //     title: errorMessage,
    //     variant: "destructive",
    //   });
    //   if (setValidationError) {
    //     setValidationError(errorMessage);
    //   }
    //   return;
    // }

    const fileObjects = validDocuments
      .filter((doc) => doc.file)
      .map((doc) => doc.file!)
      .filter(Boolean);

    const fileMetadata = documents.map((doc) => ({
      id: doc.id,
      name: doc.name,
      type: doc.type,
      category: doc.category,
      subCategory: doc.subCategory,
      categoryCode: doc.categoryCode,
      subCategoryCode: doc.subCategoryCode,
      size: doc.size,
      needsReupload: doc.needsReupload || false,
      url: doc.url,
    }));

    const finalFormData = {
      documents: documents.map((doc) => ({
        ...doc,
        file: doc.file ? true : undefined,
      })),
      fileObjects: fileObjects,
      fileMetadata: fileMetadata,
      validFileCount: validDocuments.length,
      totalFileCount: documents.length,
      isStepComplete: validDocuments.length > 0,
      submittedAt: new Date().toISOString(),
    };

    saveStepData(finalFormData);

    // Only upload if there are new files
    if (fileObjects.length > 0) {
      const formData = new FormData();
      formData.append("remote_utility_id", remoteUtilityId.toString());

      const primaryConsumerKyc = [];
      let fileIndex = 1;

      documents.forEach((doc) => {
        if (doc.file) {
          const fileKey = `file${fileIndex}`;
          formData.append(fileKey, doc.file);

          primaryConsumerKyc.push({
            id: null,
            document_type: doc.category,
            document_subtype: doc.subCategory,
            file_key: fileKey,
            consumer_role: "Primary",
          });
          fileIndex++;
        }
      });

      const documentPayload = { primary_consumer_kyc: primaryConsumerKyc };
      formData.append("document", JSON.stringify(documentPayload));

      console.log("📤 Uploading documents:", documentPayload);

      editConsumerMutation.mutate({
        consumerId: consumerId?.toString(),
        data: formData,
      });

      if (logTimeUntilStep) {
        logTimeUntilStep("Document Upload");
      }
    } else {
      console.log("ℹ️ No new files to upload");
      toast({
        title: "No changes detected",
        description: "Documents are unchanged. Proceeding to next step.",
      });
    }

    if (onNext) {
      onNext();
    }
  }, [
    documents,
    clearValidationError,
    setValidationError,
    saveStepData,
    onNext,
    consumerId,
    remoteUtilityId,
    editConsumerMutation,
    logTimeUntilStep,
  ]);

  if (isEditMode && isLoadingConsumerDetails) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent>
          <DocumentUploadForm
            initialData={documents}
            consumerId={consumerId}
            consumerDetails={consumerDetails}
            onSave={handleDocumentSave}
            onDataChange={onDataChange}
            isEditMode={isEditMode}
            showSubmitButton={false}
            remoteUtilityId={remoteUtilityId}
          />
        </CardContent>
      </Card>
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onPrevious}>
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isValidating || isSubmitting}
          className="min-w-[200px] px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {isValidating || isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Processing...
            </>
          ) : (
            "Continue to Plan Selection"
          )}
        </Button>
      </div>
    </div>
  );
}